import type { FC } from "react";
import { useState } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { QueryBarWebsiteItem, renderDefaultTextComponent } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { QueryBarItem } from "app/components/compare/QueryBarItem";
import type { IChosenItem } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { getQueryBarSecondaryTextMap } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { LoadingComponent } from "app/components/compare/WebsiteQueryBar/WebsiteQueryBar";
import { CHART_COLORS } from "app/constants/ChartColors";
import { ClickOutsideWrapper } from "app/custom-hooks/useClickOutside";
import { translate } from "app/filters/i18nFilter";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { AutocompleteWebsitePagesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitePageCompareItem";
import type { IWebsitePageResult } from "pro-features/components/AutocompleteWebsites/types";

import { QueryBarCompareButton } from "./QueryBarCompareButton";
import { VS } from "./StyledComponents";
import type { WebsitePageResults } from "./types";
import type { GetPrimarySiteReturnType } from "./utils";
import { getUpdatedCompetitorsOnAddCompetitor, trackAutocompleteOpen, trackAutocompleteSelectSite } from "./utils";

const colors = CHART_COLORS.chartMainColors;

const QueryBarWebsiteItemStyled = styled(QueryBarWebsiteItem)<{ $isLast?: boolean }>`
    background-color: ${colorsPalettes.carbon[50]};
    max-width: 170px;
    @media (max-width: 1280px) {
        max-width: 180px;
    }
    ${({ $isLast }) => $isLast && `margin-left: 16px;`}
`;

interface ICoreInlineWebsiteQueryBarProps {
    // Primary site to display
    primarySite: GetPrimarySiteReturnType;
    // Initial competitors list
    initialCompetitors?: WebsitePageResults;
    // The maximum number of competitors allowed in the query bar
    maxAllowedCompetitors?: number;
    // Callback that's called when competitors change
    onCompetitorsChange?: (competitors: WebsitePageResults) => void;
    // Callback that's called when a competitor is added with the updated competitors list
    onCompetitorAdded?: (competitors: WebsitePageResults, addedCompetitor: IWebsitePageResult) => void;
    // Callback that's called when a competitor is removed with the updated competitors list
    onCompetitorRemoved?: (competitors: WebsitePageResults, removedCompetitor: string) => void;
    // Whether the component should be in a loading state
    isLoading?: boolean;
    // Optional list of similar sites to display in the autocomplete
    similarSitesData?: ISimilarInfoItem[];
}

/**
 * CoreInlineWebsiteQueryBar is a React functional component that provides a query bar
 * for comparing websites. It allows users to add, remove, and manage competitors
 * for a primary website in a comparison context.
 *
 * This is a pure props-based component with no URL-specific logic.
 *
 * @component
 * @param {ICoreInlineWebsiteQueryBarProps} props - The props for the component.
 * @param {GetPrimarySiteReturnType} props.primarySite - The primary site to display.
 * @param {WebsitePageResults} [props.initialCompetitors=[]] - Initial competitors list.
 * @param {number} [props.maxAllowedCompetitors] - The maximum number of competitors allowed in the query bar.
 * @param {Function} [props.onCompetitorsChange] - Callback for when competitors change.
 * @param {Function} [props.onCompetitorAdded] - A callback function that's called when a competitor is added.
 * @param {Function} [props.onCompetitorRemoved] - A callback function that's called when a competitor is removed.
 * @param {boolean} [props.isLoading=false] - Whether the component should be in a loading state.
 * @param {ISimilarInfoItem[]} [props.similarSitesData] - Optional list of similar sites to display in the autocomplete.
 *
 * @returns {JSX.Element} The rendered CoreInlineWebsiteQueryBar component.
 *
 * @example
 * <CoreInlineWebsiteQueryBar
 *   primarySite={{ url: "example.com", icon: "https://example.com/favicon.ico" }}
 *   initialCompetitors={[{ name: "competitor.com", icon: "https://competitor.com/favicon.ico" }]}
 *   onCompetitorsChange={(competitors) => console.log('Competitors changed:', competitors)}
 * />
 */
export const CoreInlineWebsiteQueryBar: FC<ICoreInlineWebsiteQueryBarProps> = (props) => {
    const {
        primarySite,
        initialCompetitors = [],
        maxAllowedCompetitors,
        onCompetitorsChange,
        onCompetitorAdded,
        onCompetitorRemoved,
        isLoading: externalIsLoading = false,
        similarSitesData,
    } = props;

    const [competitors, setCompetitors] = useState<WebsitePageResults>(initialCompetitors);
    const [selectedCompetitor, setSelectedCompetitor] = useState<string | null>(null);

    const similarSites = similarSitesData || (chosenSites.similarSitesData() as ISimilarInfoItem[]);

    const clearSelectedCompetitor = () => setSelectedCompetitor(null);

    const onRemoveCompetitor = (domain: string) => () => {
        setCompetitors((prev) => {
            const updatedCompetitors = prev.filter((competitor) => competitor.name !== domain);

            // Call external callbacks
            if (onCompetitorsChange) {
                onCompetitorsChange(updatedCompetitors);
            }

            if (onCompetitorRemoved) {
                onCompetitorRemoved(updatedCompetitors, domain);
            }

            return updatedCompetitors;
        });
    };

    const onEditCompetitor = (competitorToAdd: IWebsitePageResult, indexToReplace: number) => {
        trackAutocompleteSelectSite(competitorToAdd.name);
        const updatedCompetitors = getUpdatedCompetitorsOnAddCompetitor(competitors, competitorToAdd, indexToReplace);

        if (onCompetitorsChange) {
            onCompetitorsChange(updatedCompetitors);
        }

        if (onCompetitorAdded) {
            onCompetitorAdded(updatedCompetitors, competitorToAdd);
        }

        setCompetitors(updatedCompetitors);
        clearSelectedCompetitor();
    };

    // Handler for adding a competitor
    const onAddCompetitor = (competitorToAdd: IWebsitePageResult) => {
        const activeCompetitorIndex = competitors.findIndex((comp) => comp.name === selectedCompetitor);

        if (activeCompetitorIndex !== -1) {
            onEditCompetitor(competitorToAdd, activeCompetitorIndex);
        } else {
            setCompetitors((prev) => {
                const updatedCompetitors = [...prev, competitorToAdd];

                if (onCompetitorsChange) {
                    onCompetitorsChange(updatedCompetitors);
                }

                if (onCompetitorAdded) {
                    onCompetitorAdded(updatedCompetitors, competitorToAdd);
                }

                return updatedCompetitors;
            });
        }
    };

    const sitesToExcludeFromAutocomplete = [
        { ...primarySite, name: primarySite.url },
        ...(competitors?.map((c) => ({ ...c, url: c.name }) as IChosenItem) ?? []),
    ].filter(Boolean);

    const showLoading = externalIsLoading;

    return (
        <Flex
            gap="8px"
            alignItems="center"
            data-automation="inline-website-query-bar"
        >
            <QueryBarWebsiteItemStyled
                text={primarySite.url}
                image={primarySite.icon}
                badgeColor={competitors.length > 0 ? colors[0] : undefined}
                isCompare={true}
                hideRemoveButton={true}
                onItemClick={() => void 0}
                isDisabled={true}
                secondaryText={getQueryBarSecondaryTextMap()[primarySite?.searchType ?? "domain"]}
            />
            {competitors?.length > 0 ? <VS /> : null}
            {competitors?.map(({ name, icon, searchType, secondaryText }, index) => (
                <QueryBarItem
                    key={name}
                    id={name}
                    isLoading={showLoading}
                    activeItem={selectedCompetitor}
                    renderComponent={
                        <QueryBarWebsiteItemStyled
                            text={name}
                            image={icon ?? ""}
                            onItemClick={() => setSelectedCompetitor(name)}
                            onButtonClick={onRemoveCompetitor(name)}
                            badgeColor={colors[index + 1]}
                            isCompare={true}
                            secondaryText={secondaryText ?? getQueryBarSecondaryTextMap()[searchType]}
                            textRenderFunction={(...args) => {
                                const isPageLevel = searchType === "exact" || searchType === "folder";
                                return renderDefaultTextComponent(
                                    args[0],
                                    isPageLevel ? (args[1] ?? "") : (args[1]?.split("/")[0] ?? ""),
                                    args[2],
                                    isPageLevel,
                                );
                            }}
                        />
                    }
                    searchComponent={
                        <ClickOutsideWrapper onClickOutside={clearSelectedCompetitor}>
                            <AutocompleteWebsitePagesCompareItem
                                className="AutocompleteInlineWebsiteQueryBar"
                                secondColumnName="secondaryText"
                                onClick={onAddCompetitor}
                                similarSites={similarSites}
                                defaultValue={searchType !== "domain" && searchType !== "subdomain" ? name : undefined}
                                excludes={sitesToExcludeFromAutocomplete as IChosenItem[]}
                                onCloseButtonClick={clearSelectedCompetitor}
                                overrideEnterFunc={(_, item) => {
                                    if (item) {
                                        onAddCompetitor(item as IWebsitePageResult);
                                    }
                                    clearSelectedCompetitor();
                                }}
                                autocompleteProps={{
                                    placeholder: `${selectedCompetitor ? selectedCompetitor : ""}`,
                                    width: 400,
                                    onFocus: () => {
                                        trackAutocompleteOpen();
                                    },
                                }}
                            />
                        </ClickOutsideWrapper>
                    }
                    loadingComponent={
                        <LoadingComponent
                            placeholderText={name}
                            headerText={translate("autocomplete.websitePagesCompare.seperator.text.similarSites")}
                        />
                    }
                />
            ))}
            <QueryBarCompareButton
                currentCompetitors={competitors}
                onAddCompetitor={onAddCompetitor}
                onClearCompetitors={() => {
                    setCompetitors([]);
                    if (onCompetitorsChange) {
                        onCompetitorsChange([]);
                    }
                }}
                similarSites={similarSites}
                sitesToExclude={sitesToExcludeFromAutocomplete as IChosenItem[]}
                maxAllowedCompetitors={maxAllowedCompetitors}
            />
        </Flex>
    );
};
